import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { getAllTickets, updateTicketStatus } from '../api';
import { Ticket, TicketStatus, TicketPriority } from '../types/ticket';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';

const TicketList: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<TicketStatus>(TicketStatus.OPEN);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);

  useEffect(() => {
    loadTickets();
  }, [selectedStatus, searchQuery]);

  const loadTickets = async () => {
    try {
      const data = await getAllTickets({
        status: searchQuery ? undefined : selectedStatus,
        search: searchQuery || undefined
      });
      setTickets(data);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load tickets',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const priorityBodyTemplate = (ticket: Ticket) => {
    const severityMap: Record<TicketPriority, 'success' | 'warning' | 'danger'> = {
      [TicketPriority.LOW]: 'success',
      [TicketPriority.MEDIUM]: 'warning',
      [TicketPriority.HIGH]: 'danger'
    };

    return (
      <Tag 
        value={ticket.priority} 
        severity={severityMap[ticket.priority]}
      />
    );
  };

  const statusBodyTemplate = (ticket: Ticket) => {
    const severityMap: Record<TicketStatus, 'info' | 'warning' | 'success'> = {
      [TicketStatus.OPEN]: 'info',
      [TicketStatus.IN_PROGRESS]: 'warning',
      [TicketStatus.CLOSED]: 'success'
    };

    return (
      <Tag 
        value={ticket.status} 
        severity={severityMap[ticket.status]}
      />
    );
  };

  const handleStatusChange = async (ticket: Ticket, newStatus: TicketStatus) => {
    try {
      await updateTicketStatus(ticket.id, newStatus);
      await loadTickets();
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Ticket status updated successfully',
        life: 3000
      });
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update ticket status',
        life: 3000
      });
    }
  };

  const actionBodyTemplate = (ticket: Ticket) => {
    return (
      <div className="flex gap-2">
        {ticket.status !== TicketStatus.CLOSED && (
          <Button
            icon="pi pi-lock"
            rounded
            outlined
            className="p-button-warning"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange(ticket, TicketStatus.CLOSED);
            }}
            tooltip="Close Ticket"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {ticket.status === TicketStatus.CLOSED && (
          <Button
            icon="pi pi-lock-open"
            rounded
            outlined
            className="p-button-info"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange(ticket, TicketStatus.OPEN);
            }}
            tooltip="Reopen Ticket"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="p-button-success"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/tickets/edit/${ticket.id}`);
          }}
        />
      </div>
    );
  };

  const dateBodyTemplate = (ticket: Ticket, field: 'createdAt' | 'updatedAt') => {
    return new Date(ticket[field]).toLocaleDateString();
  };

  return (
    <div className="card">
      <Toast ref={toast} />
      <ConfirmDialog />
      
      <div className="flex flex-column gap-3 mb-3">
        <div className="flex justify-content-between">
          <h1>Tickets</h1>
          <Button
            label="Create Ticket"
            icon="pi pi-plus"
            onClick={() => navigate('/tickets/create')}
          />
        </div>

        <div className="flex flex-column gap-2">
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              placeholder="Search tickets..."
              className="w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </span>

          <TabView
            activeIndex={activeTab}
            onTabChange={(e) => {
              setActiveTab(e.index);
              if (e.index < 2) { // Only handle OPEN/CLOSED tabs
                setSearchQuery(''); // Clear search when switching to status tabs
                setSelectedStatus(e.index === 0 ? TicketStatus.OPEN : TicketStatus.CLOSED);
              }
            }}
          >
            <TabPanel header="Open Tickets" disabled={!!searchQuery}>
              {/* TabPanel content is rendered through the DataTable below */}
            </TabPanel>
            <TabPanel header="Closed Tickets" disabled={!!searchQuery}>
              {/* TabPanel content is rendered through the DataTable below */}
            </TabPanel>
            {searchQuery && (
              <TabPanel header={`Search Results (${tickets.length})`}>
                {/* TabPanel content is rendered through the DataTable below */}
              </TabPanel>
            )}
          </TabView>
        </div>
      </div>

      <DataTable
        value={tickets}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        dataKey="id"
        responsiveLayout="scroll"
        emptyMessage="No tickets found."
        onRowClick={(e) => navigate(`/tickets/${(e.data as Ticket).id}`)}
        selectionMode="single"
        className="cursor-pointer"
      >
        <Column field="title" header="Title" sortable filter />
        <Column field="priority" header="Priority" body={priorityBodyTemplate} sortable filter />
        <Column field="status" header="Status" body={statusBodyTemplate} sortable filter />
        <Column 
          field="createdAt" 
          header="Created" 
          sortable 
          body={(ticket) => dateBodyTemplate(ticket, 'createdAt')}
        />
        <Column 
          field="assignedTo.username" 
          header="Assigned To" 
          sortable 
          filter 
        />
        <Column 
          field="createdBy.username" 
          header="Created By" 
          sortable 
          filter 
        />
        <Column
          field="attachments"
          header="Attachments"
          body={(ticket: Ticket) => {
            if (!ticket.attachments?.length) return null;
            return (
              <Tag
                value={ticket.attachments.length}
                icon="pi pi-paperclip"
                severity="info"
              />
            );
          }}
          style={{ width: '8rem', textAlign: 'center' }}
        />
        <Column body={actionBodyTemplate} exportable={false} style={{ width: '10rem' }} />
      </DataTable>
    </div>
  );
};

export default TicketList;
